import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Toaster, toast } from 'sonner';
import { LogOut, Plus, Edit2, Trash2, Save, X, AlertTriangle, Search, Database, ChevronRight } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const PUB = `${BACKEND}/api/tuning`;
const ADM = `${BACKEND}/api/admin`;

const authHeader = () => {
  const t = localStorage.getItem('adminToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// Top-level Column component (kept outside AdminPanel to preserve internal state
// across parent re-renders — inline function components lose state on every render).
const Column = ({
  title, items, selected, onSelect, colKey,
  search, setSearch, adding, setAdding, editing, setEditing,
  onAdd, onUpdate, onOpenDelete, extraFields, isEnabled,
}) => {
  const filter = (search || '').toLowerCase();
  const filtered = items.filter(i => (i.name || '').toLowerCase().includes(filter));

  return (
    <div className="flex flex-col h-full border-r border-gray-800 min-w-0 bg-[#0d0d0d]">
      <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between gap-2">
        <h3 className="text-white font-semibold text-sm uppercase tracking-wider">
          {title} <span className="text-gray-500 text-xs font-normal">({items.length})</span>
        </h3>
        <Button
          size="sm"
          disabled={!isEnabled}
          onClick={() => setAdding({ name: '', ...(extraFields ? extraFields.defaults : {}) })}
          className="bg-[#FF6600] hover:bg-[#E55A00] text-white h-7 px-2 disabled:opacity-30"
          data-testid={`add-${colKey}-btn`}
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      <div className="px-3 py-2 border-b border-gray-800">
        <div className="relative">
          <Search className="w-4 h-4 text-gray-500 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="bg-black/50 border-gray-700 text-white h-8 pl-8"
            data-testid={`search-${colKey}`}
          />
        </div>
      </div>
      {adding && (
        <div className="px-3 py-2 border-b border-gray-800 bg-[#FF6600]/5 space-y-2">
          <Input
            autoFocus
            placeholder="Name"
            value={adding.name}
            onChange={(e) => setAdding({ ...adding, name: e.target.value })}
            className="bg-black/50 border-gray-700 text-white h-8"
            data-testid={`new-${colKey}-name`}
          />
          {extraFields && extraFields.render(adding, setAdding, 'new')}
          <div className="flex gap-1">
            <Button
              size="sm"
              onClick={async () => { await onAdd(adding); setAdding(null); }}
              disabled={!adding.name?.trim()}
              className="bg-green-600 hover:bg-green-700 text-white h-7 px-2 flex-1"
              data-testid={`save-new-${colKey}`}
            >
              <Save className="w-3 h-3 mr-1" /> Save
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setAdding(null)} className="text-gray-400 h-7 px-2">
              <X className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}
      <div className="flex-1 overflow-y-auto">
        {isEnabled && filtered.length === 0 && (
          <div className="p-4 text-gray-600 text-xs text-center">No items</div>
        )}
        {!isEnabled && (
          <div className="p-4 text-gray-600 text-xs text-center">Select from previous column</div>
        )}
        {filtered.map(item => {
          const isEditing = editing?.id === item.id;
          const isSelected = selected?.id === item.id;
          return (
            <div
              key={item.id}
              className={`group border-b border-gray-900 ${isSelected ? 'bg-[#FF6600]/15 border-l-2 border-l-[#FF6600]' : 'hover:bg-white/5'}`}
            >
              {isEditing ? (
                <div className="px-3 py-2 space-y-2">
                  <Input
                    autoFocus
                    value={editing.body.name}
                    onChange={(e) => setEditing({ ...editing, body: { ...editing.body, name: e.target.value } })}
                    className="bg-black/50 border-gray-700 text-white h-8"
                    data-testid={`edit-${colKey}-name`}
                  />
                  {extraFields && extraFields.render(editing.body, (updater) => {
                    setEditing({ ...editing, body: typeof updater === 'function' ? updater(editing.body) : updater });
                  }, 'edit')}
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      onClick={async () => { await onUpdate(item.id, editing.body); setEditing(null); }}
                      className="bg-green-600 hover:bg-green-700 text-white h-7 px-2 flex-1"
                      data-testid={`save-edit-${colKey}`}
                    >
                      <Save className="w-3 h-3 mr-1" /> Save
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(null)} className="text-gray-400 h-7 px-2">
                      <X className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  className="flex items-center px-3 py-2 cursor-pointer"
                  onClick={() => onSelect(item)}
                  data-testid={`${colKey}-row-${item.id}`}
                >
                  <span className={`flex-1 text-sm truncate ${isSelected ? 'text-white font-semibold' : 'text-gray-300'}`}>
                    {item.name}
                    {(item.year_begin || item.year_end) && (
                      <span className="ml-2 text-xs text-gray-500">
                        ({item.year_begin || '?'}–{item.year_end || 'now'})
                      </span>
                    )}
                  </span>
                  <div className="opacity-0 group-hover:opacity-100 flex gap-1 transition mr-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditing({ id: item.id, body: { name: item.name, year_begin: item.year_begin, year_end: item.year_end } }); }}
                      className="p-1 text-gray-400 hover:text-white"
                      data-testid={`edit-${colKey}-${item.id}`}
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); onOpenDelete(item); }}
                      className="p-1 text-gray-400 hover:text-red-400"
                      data-testid={`delete-${colKey}-${item.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <ChevronRight className={`w-4 h-4 ${isSelected ? 'text-[#FF6600]' : 'text-gray-700'}`} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const AdminPanel = () => {
  const nav = useNavigate();

  useEffect(() => {
    document.title = 'Admin Panel | Powerlab Works';
    const m = document.createElement('meta');
    m.name = 'robots';
    m.content = 'noindex,nofollow';
    m.setAttribute('data-admin-noindex', 'true');
    document.head.appendChild(m);
    return () => m.remove();
  }, []);

  // Auth guard
  useEffect(() => {
    const tok = localStorage.getItem('adminToken');
    const exp = parseInt(localStorage.getItem('adminExpiresAt') || '0', 10);
    if (!tok || Date.now() > exp) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminExpiresAt');
      nav('/admin/login', { replace: true });
    }
  }, [nav]);

  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [generations, setGenerations] = useState([]);
  const [engines, setEngines] = useState([]);

  const [selBrand, setSelBrand] = useState(null);
  const [selModel, setSelModel] = useState(null);
  const [selGen, setSelGen] = useState(null);
  const [selEngine, setSelEngine] = useState(null);

  // Per-column: search / adding / editing
  const [searchBrand, setSearchBrand] = useState('');
  const [searchModel, setSearchModel] = useState('');
  const [searchGen, setSearchGen] = useState('');
  const [searchEngine, setSearchEngine] = useState('');
  const [addingBrand, setAddingBrand] = useState(null);
  const [addingModel, setAddingModel] = useState(null);
  const [addingGen, setAddingGen] = useState(null);
  const [addingEngine, setAddingEngine] = useState(null);
  const [editingBrand, setEditingBrand] = useState(null);
  const [editingModel, setEditingModel] = useState(null);
  const [editingGen, setEditingGen] = useState(null);
  const [editingEngine, setEditingEngine] = useState(null);

  const [perf, setPerf] = useState({});
  const [specs, setSpecs] = useState({});
  const [confirmDel, setConfirmDel] = useState(null);

  const logout = useCallback(() => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminExpiresAt');
    nav('/admin/login', { replace: true });
  }, [nav]);

  const handle401 = useCallback((e) => {
    if (e.response?.status === 401) { toast.error('Session expired'); logout(); return true; }
    return false;
  }, [logout]);

  const load = useCallback(async (label, url, setter) => {
    try {
      const r = await axios.get(url);
      setter(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(`Failed to load ${label}`);
      setter([]);
    }
  }, []);

  const reloadBrands = useCallback(() => load('brands', `${PUB}/brands`, setBrands), [load]);
  const reloadModels = useCallback(() => selBrand && load('models', `${PUB}/brands/${selBrand.id}/models`, setModels), [load, selBrand]);
  const reloadGens = useCallback(() => selModel && load('generations', `${PUB}/models/${selModel.id}/generations`, setGenerations), [load, selModel]);
  const reloadEngines = useCallback(() => selGen && load('engines', `${PUB}/generations/${selGen.id}/engines`, setEngines), [load, selGen]);

  useEffect(() => { reloadBrands(); }, [reloadBrands]);

  useEffect(() => {
    setSelModel(null); setSelGen(null); setSelEngine(null);
    setModels([]); setGenerations([]); setEngines([]);
    if (selBrand) reloadModels();
  }, [selBrand, reloadModels]);

  useEffect(() => {
    setSelGen(null); setSelEngine(null);
    setGenerations([]); setEngines([]);
    if (selModel) reloadGens();
  }, [selModel, reloadGens]);

  useEffect(() => {
    setSelEngine(null);
    setEngines([]);
    if (selGen) reloadEngines();
  }, [selGen, reloadEngines]);

  useEffect(() => {
    if (!selEngine) { setPerf({}); setSpecs({}); return; }
    axios.get(`${ADM}/engines/${selEngine.id}/full`, { headers: authHeader() })
      .then(r => {
        setPerf(r.data.performance || {});
        setSpecs(r.data.specs || {});
      })
      .catch(e => { if (!handle401(e)) toast.error('Failed to load engine details'); });
  }, [selEngine, handle401]);

  const upsert = async (method, url, body, onSuccess) => {
    try {
      await axios({ method, url, data: body, headers: authHeader() });
      toast.success('Saved');
      onSuccess?.();
    } catch (e) {
      if (handle401(e)) return;
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const openDelete = async (type, item, impactUrl) => {
    let impact = null;
    if (impactUrl) {
      try {
        const r = await axios.get(impactUrl, { headers: authHeader() });
        impact = r.data;
      } catch (e) {
        if (handle401(e)) return;
      }
    }
    setConfirmDel({ type, id: item.id, name: item.name, impact });
  };

  const doDelete = async () => {
    const { type, id } = confirmDel;
    const paths = { brand: 'brands', model: 'models', gen: 'generations', engine: 'engines' };
    try {
      await axios.delete(`${ADM}/${paths[type]}/${id}`, { headers: authHeader() });
      toast.success('Deleted');
      setConfirmDel(null);
      if (type === 'brand') { reloadBrands(); setSelBrand(null); }
      if (type === 'model') { reloadModels(); setSelModel(null); }
      if (type === 'gen') { reloadGens(); setSelGen(null); }
      if (type === 'engine') { reloadEngines(); setSelEngine(null); }
    } catch (e) {
      if (handle401(e)) return;
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const savePerformance = async () => {
    if (!selEngine) return;
    const body = { ...perf };
    delete body.engine_id;
    await upsert('put', `${ADM}/engines/${selEngine.id}/performance`, body, () => {
      axios.get(`${ADM}/engines/${selEngine.id}/full`, { headers: authHeader() })
        .then(r => setPerf(r.data.performance || {}));
    });
  };
  const saveSpecs = async () => {
    if (!selEngine) return;
    const body = { ...specs };
    delete body.engine_id;
    await upsert('put', `${ADM}/engines/${selEngine.id}/specs`, body);
  };

  const yearFields = {
    defaults: { year_begin: null, year_end: null },
    render: (body, setBody, mode) => (
      <div className="flex gap-1">
        <Input
          type="number" placeholder="From year"
          value={body.year_begin ?? ''}
          onChange={(e) => setBody({ ...body, year_begin: e.target.value ? parseInt(e.target.value, 10) : null })}
          className="bg-black/50 border-gray-700 text-white h-8 text-xs"
          data-testid={`gen-year-begin-${mode}`}
        />
        <Input
          type="number" placeholder="To year"
          value={body.year_end ?? ''}
          onChange={(e) => setBody({ ...body, year_end: e.target.value ? parseInt(e.target.value, 10) : null })}
          className="bg-black/50 border-gray-700 text-white h-8 text-xs"
          data-testid={`gen-year-end-${mode}`}
        />
      </div>
    ),
  };

  return (
    <div className="min-h-screen bg-black flex flex-col">
      <Toaster theme="dark" position="top-right" richColors />
      <header className="bg-[#0a0a0a] border-b border-gray-800 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Database className="w-5 h-5 text-[#FF6600]" />
          <h1 className="text-white font-bold text-lg">Powerlab Admin</h1>
          <span className="text-xs text-gray-500 hidden md:inline">
            {brands.length} brands · {models.length} models · {generations.length} gens · {engines.length} engines
          </span>
        </div>
        <Button onClick={logout} variant="ghost" className="text-gray-400 hover:text-white" data-testid="admin-logout-btn">
          <LogOut className="w-4 h-4 mr-2" /> Logout
        </Button>
      </header>

      <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 min-h-[500px]" style={{ maxHeight: 'calc(100vh - 60px - 380px)' }}>
        <Column
          title="Brands" colKey="brand" items={brands} selected={selBrand}
          onSelect={setSelBrand} isEnabled={true}
          search={searchBrand} setSearch={setSearchBrand}
          adding={addingBrand} setAdding={setAddingBrand}
          editing={editingBrand} setEditing={setEditingBrand}
          onAdd={async (body) => upsert('post', `${ADM}/brands`, { name: body.name }, reloadBrands)}
          onUpdate={async (id, body) => upsert('put', `${ADM}/brands/${id}`, { name: body.name }, reloadBrands)}
          onOpenDelete={(item) => openDelete('brand', item, `${ADM}/impact/brands/${item.id}`)}
        />
        <Column
          title="Models" colKey="model" items={models} selected={selModel}
          onSelect={setSelModel} isEnabled={!!selBrand}
          search={searchModel} setSearch={setSearchModel}
          adding={addingModel} setAdding={setAddingModel}
          editing={editingModel} setEditing={setEditingModel}
          onAdd={async (body) => selBrand && upsert('post', `${ADM}/models`, { brand_id: selBrand.id, name: body.name }, reloadModels)}
          onUpdate={async (id, body) => upsert('put', `${ADM}/models/${id}`, { name: body.name }, reloadModels)}
          onOpenDelete={(item) => openDelete('model', item, `${ADM}/impact/models/${item.id}`)}
        />
        <Column
          title="Generations" colKey="gen" items={generations} selected={selGen}
          onSelect={setSelGen} isEnabled={!!selModel}
          search={searchGen} setSearch={setSearchGen}
          adding={addingGen} setAdding={setAddingGen}
          editing={editingGen} setEditing={setEditingGen}
          extraFields={yearFields}
          onAdd={async (body) => selModel && upsert('post', `${ADM}/generations`, {
            model_id: selModel.id, name: body.name, year_begin: body.year_begin, year_end: body.year_end,
          }, reloadGens)}
          onUpdate={async (id, body) => upsert('put', `${ADM}/generations/${id}`, {
            name: body.name, year_begin: body.year_begin, year_end: body.year_end,
          }, reloadGens)}
          onOpenDelete={(item) => openDelete('gen', item, `${ADM}/impact/generations/${item.id}`)}
        />
        <Column
          title="Engines" colKey="engine" items={engines} selected={selEngine}
          onSelect={setSelEngine} isEnabled={!!selGen}
          search={searchEngine} setSearch={setSearchEngine}
          adding={addingEngine} setAdding={setAddingEngine}
          editing={editingEngine} setEditing={setEditingEngine}
          onAdd={async (body) => selGen && upsert('post', `${ADM}/engines`, { generation_id: selGen.id, name: body.name }, reloadEngines)}
          onUpdate={async (id, body) => upsert('put', `${ADM}/engines/${id}`, { name: body.name }, reloadEngines)}
          onOpenDelete={(item) => openDelete('engine', item, null)}
        />
      </div>

      {selEngine && (
        <div className="border-t-2 border-[#FF6600] bg-[#0a0a0a] p-4" data-testid="engine-detail-section">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-white font-bold text-lg truncate">
              {selBrand?.name} · {selModel?.name} · {selGen?.name} · <span className="text-[#FF6600]">{selEngine?.name}</span>
            </h2>
            <span className="text-xs text-gray-500">Engine #{selEngine.id}</span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card className="bg-[#1a1a1a] border-gray-800">
              <CardContent className="p-4">
                <h3 className="text-white font-semibold mb-3 text-sm uppercase tracking-wider">Performance</h3>
                <div className="grid grid-cols-2 gap-2">
                  {['power_original', 'power_tuned', 'power_diff', 'torque_original', 'torque_tuned', 'torque_diff'].map(k => (
                    <div key={k}>
                      <label className="text-[10px] text-gray-500 uppercase tracking-wider">{k.replace(/_/g, ' ')}</label>
                      <Input
                        type="number" step="0.1"
                        value={perf[k] ?? ''}
                        onChange={(e) => setPerf(p => ({ ...p, [k]: e.target.value === '' ? null : parseFloat(e.target.value) }))}
                        className="bg-black/50 border-gray-700 text-white h-8"
                        data-testid={`perf-${k}`}
                      />
                    </div>
                  ))}
                </div>
                <Button onClick={savePerformance} className="mt-3 bg-[#FF6600] hover:bg-[#E55A00] text-white w-full" data-testid="save-perf-btn">
                  <Save className="w-4 h-4 mr-2" /> Save Performance
                </Button>
                <p className="text-[11px] text-gray-500 mt-2">Leave diff empty to auto-calculate from tuned − original.</p>
              </CardContent>
            </Card>
            <Card className="bg-[#1a1a1a] border-gray-800">
              <CardContent className="p-4">
                <h3 className="text-white font-semibold mb-3 text-sm uppercase tracking-wider">Technical Specs</h3>
                <div className="grid grid-cols-2 gap-2">
                  {['fuel_type', 'method', 'tuning_type', 'cylinder_content', 'compression_ratio', 'bore_stroke', 'engine_number', 'engine_ecu'].map(k => (
                    <div key={k}>
                      <label className="text-[10px] text-gray-500 uppercase tracking-wider">{k.replace(/_/g, ' ')}</label>
                      <Input
                        value={specs[k] ?? ''}
                        onChange={(e) => setSpecs(s => ({ ...s, [k]: e.target.value }))}
                        className="bg-black/50 border-gray-700 text-white h-8"
                        data-testid={`specs-${k}`}
                      />
                    </div>
                  ))}
                </div>
                <Button onClick={saveSpecs} className="mt-3 bg-[#FF6600] hover:bg-[#E55A00] text-white w-full" data-testid="save-specs-btn">
                  <Save className="w-4 h-4 mr-2" /> Save Specs
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {confirmDel && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setConfirmDel(null)} data-testid="delete-confirm-modal">
          <Card className="w-full max-w-md bg-[#1a1a1a] border-red-500/40" onClick={(e) => e.stopPropagation()}>
            <CardContent className="p-6">
              <div className="flex items-start gap-3 mb-4">
                <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-white font-bold text-lg">Delete {confirmDel.type}?</h3>
                  <p className="text-gray-400 text-sm mt-1">
                    "<span className="text-white font-semibold">{confirmDel.name}</span>" will be permanently removed.
                  </p>
                </div>
              </div>
              {confirmDel.impact && Object.values(confirmDel.impact).some(v => v > 0) && (
                <div className="bg-red-500/10 border border-red-500/40 rounded-lg p-3 mb-4">
                  <p className="text-red-300 text-sm font-semibold mb-2">Cascade delete impact:</p>
                  <ul className="text-red-200 text-sm space-y-0.5">
                    {Object.entries(confirmDel.impact).map(([k, v]) => v > 0 && <li key={k}>• {v} {k}</li>)}
                  </ul>
                </div>
              )}
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => setConfirmDel(null)} className="flex-1 text-gray-300 hover:text-white" data-testid="delete-cancel-btn">
                  Cancel
                </Button>
                <Button onClick={doDelete} className="flex-1 bg-red-600 hover:bg-red-700 text-white" data-testid="delete-confirm-btn">
                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
